import { createAsyncThunk } from '@reduxjs/toolkit'
import callAPI from 'api'

import { Industry, FixMeAny } from 'types/global'

import {
  GetDurationsResponse,
  GetFiltersResponse,
  GetLeadSourceTypesResponse,
  GetLookupsResponse,
  GetSalesCampaignsPayload,
  GetSalesCampaignsResponse,
  GetSalesSourcesPayload,
  GetSalesSourcesResponse,
  FiltersRequestPayload,
  LookupRequestPayload,
  UsersRequestPayload,
  GetMeetingMinutesLookupResponse,
} from 'services/types/lookup'
import { GetUsersResponse } from 'services/types/user'
import { getMappedUserData } from 'store/user'
import { Fail, Success } from 'store/types'

/**
 * Async function to get industries for selected master
 */
export const getMasterIndustries = createAsyncThunk('lookup/getMasterIndustries', async () => {
  const response = await callAPI('crm_get_master_industries', undefined, { requireAuthorization: true })

  if (response.status === 'SUCCESS') {
    const industries: Industry[] = response.data.map((r: FixMeAny) => ({
      industryId: r.id,
      industryName: r.name,
    }))

    return industries
  }
})

/**
 * Async function to get industries for selected master
 */
export const getCoreIndustries = createAsyncThunk('lookup/getCoreIndustries', async () => {
  const response = await callAPI('crm_get_core_industries', undefined, { requireAuthorization: true })

  if (response.status === 'SUCCESS') {
    const industries: Industry[] = response.data.map((r: FixMeAny) => ({
      industryId: r.id,
      industryName: r.name,
    }))

    return industries
  }
})

/**
 * Async function to get lead campaign for selected master
 */
export const getLeadCampaigns = createAsyncThunk<Fail | Success<GetSalesCampaignsResponse>, GetSalesCampaignsPayload>(
  'lookup/getLeadCampaigns',
  async (payload: GetSalesCampaignsPayload) =>
    await callAPI<GetSalesCampaignsResponse, GetSalesCampaignsPayload>('crm_get_lead_campaigns', [payload], {
      requireAuthorization: true,
    }),
)

/**
 * Async function to get lead source for selected master
 */
export const getLeadSources = createAsyncThunk<Fail | Success<GetSalesSourcesResponse>, GetSalesSourcesPayload>(
  'lookup/getLeadSources',
  async (payload: GetSalesSourcesPayload) =>
    await callAPI<GetSalesSourcesResponse, GetSalesSourcesPayload>('crm_get_lead_sources', [payload], {
      requireAuthorization: true,
    }),
)

/**
 * Async function to fetch lead source types
 */
export const getLeadSourceTypes = createAsyncThunk<Fail | Success<GetLeadSourceTypesResponse>>(
  'lookup/getLeadSourceTypes',
  async () =>
    await callAPI<GetLeadSourceTypesResponse>('crm_get_lead_source_types', undefined, { requireAuthorization: true }),
)

/**
 * Async function to get lightweight list of users for selected master
 */
export const getUsers = createAsyncThunk('lookup/getUsers', async (payload: UsersRequestPayload) => {
  const response = await callAPI<GetUsersResponse>('crm_get_users', [payload], { requireAuthorization: true })

  if (response.status === 'SUCCESS') {
    return getMappedUserData(response.data).userDetails
  }
})

/**
 * Async function to fetch lookup
 */
export const getLookup = async (payload?: LookupRequestPayload) =>
  await callAPI<GetLookupsResponse>('crm_get_lookups', [payload], {
    requireAuthorization: true,
  })

/**
 * Async function to fetch filters for leads, global search, etc
 */
export const getFilterApi = async (payload?: FiltersRequestPayload) =>
  await callAPI<GetFiltersResponse>('crm_get_filters', [payload], {
    requireAuthorization: true,
  })

/**
 * Duration Lookup
 */
export const getDurations = async () =>
  await callAPI<GetDurationsResponse>('crm_get_lead_appointment_durations', undefined, {
    requireAuthorization: true,
  })

/**
 * Meeting minute config
 */
export const getLeadMeetingMinutesLookup = createAsyncThunk<Fail | Success<GetMeetingMinutesLookupResponse>>(
  'lookup/meetingMinutes',
  async () =>
    await callAPI<GetMeetingMinutesLookupResponse>('crm_get_lead_meeting_minute_lookup', undefined, {
      requireAuthorization: true,
    }),
)
